/**
 * Query and mutation hooks over the private branding routes (P11–P13).
 *
 * The public `GET /branding` that painted the page at boot is a different
 * route with a different job; this file is the *editor's* data layer, keyed
 * separately (`['branding','me']`) so an edit never invalidates the login
 * page's cache entry by accident.
 *
 * Both writes hold their `Idempotency-Key` per gesture — the `ExportsPage`
 * ref pattern, via the payload-fingerprinted `useGestureKey` refinement the
 * users feature established: a retry of the same save reuses the key, an
 * edited save is a new gesture, success clears it.
 */

import type { BrandingPayload, FontFamily, RadiusScale } from '@rd/shared';
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import { apiRequest } from '../../lib/apiClient.js';
import { useGestureKey } from '../users/useUsers.js';

export const BRANDING_ME_KEY = ['branding', 'me'] as const;

/**
 * The PUT body, transcribed from `branding.schema.ts`'s **strict** schema.
 * Colours are six bare hex digits — no `#`, exactly as stored (§11.3). The
 * semantic colours (danger/warning/success) are absent here because they are
 * absent there *by design*: §11.6 makes them platform-defined, and sending
 * one is a 400 naming the field, not a silent no-op.
 */
export interface UpdateBrandingBody {
  productName?: string;
  colorPrimary?: string;
  colorAccent?: string;
  colorSurface?: string;
  colorSurfaceAlt?: string;
  colorText?: string;
  colorTextMuted?: string;
  radiusScale?: RadiusScale;
  fontFamily?: FontFamily;
  supportEmail?: string | null;
  footerText?: string | null;
}

export type AssetKind = 'LOGO' | 'LOGO_DARK' | 'FAVICON' | 'LOGIN_BACKGROUND';

/** What P13 answers with (201). */
export interface UploadedAsset {
  assetRef: string;
  url: string;
  mimeType: string;
  byteSize: number;
  sanitised: string[];
}

export function useBrandingMe(): UseQueryResult<BrandingPayload> {
  return useQuery<BrandingPayload>({
    queryKey: BRANDING_ME_KEY,
    queryFn: () => apiRequest<BrandingPayload>('/branding/me'),
  });
}

/**
 * `PUT /branding/me` — **dirty fields only**. The caller builds the body from
 * what actually changed; sending the whole form would make every save claim
 * authorship of ten fields and would re-trip contrast checks the user never
 * touched.
 */
export function useUpdateBranding() {
  const queryClient = useQueryClient();
  const gesture = useGestureKey();

  return useMutation<BrandingPayload, unknown, UpdateBrandingBody>({
    mutationFn: (body) =>
      apiRequest<BrandingPayload>('/branding/me', {
        method: 'PUT',
        body,
        idempotencyKey: gesture.keyFor(JSON.stringify(body)),
      }),
    onSuccess: (payload) => {
      gesture.clear();
      // The PUT echoes the authoritative payload (warnings rebuilt from the
      // stored row), so it IS the fresh cache entry — no second round trip.
      queryClient.setQueryData(BRANDING_ME_KEY, payload);
    },
  });
}

/** `POST /branding/me/assets?kind=…`, multipart, one part named `file`. */
export function useUploadAsset() {
  const queryClient = useQueryClient();
  const gesture = useGestureKey();

  return useMutation<UploadedAsset, unknown, { kind: AssetKind; file: File }>({
    mutationFn: ({ kind, file }) => {
      const form = new FormData();
      // The field name is part of the contract: the route's multer config
      // reads exactly one part called `file` and 400s anything else.
      form.append('file', file);
      return apiRequest<UploadedAsset>('/branding/me/assets', {
        method: 'POST',
        body: form,
        query: { kind },
        // Fingerprint on what identifies the gesture: same file re-tried is
        // the same upload; a different file for the same slot is a new one.
        idempotencyKey: gesture.keyFor(`${kind}:${file.name}:${file.size}:${file.lastModified}`),
      });
    },
    onSuccess: () => {
      gesture.clear();
      // The upload bumps the stored branding (new asset URL + version), so the
      // editor's payload — previews included — is refetched rather than patched.
      void queryClient.invalidateQueries({ queryKey: BRANDING_ME_KEY });
    },
  });
}
